import { ImsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import TagFilledIcon from "@redotech/redo-icons/tag-01_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import { useNavTreeNode } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";
import { InventoryManagementSuiteStatusesContext } from "../../../../redo-suite/inventory-cloud";
import { NavigationVersionContext } from "../../navigation-version-context";
import { InventoryItem } from "./inventory";

export function useInventoryNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { newNavigationEnabled } = useContext(NavigationVersionContext);

  const pathRoot = routes.inventoryManagementBaseUrl;

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );

  const canViewInventoryManagement = useHasPermission(
    ...Object.values(ImsPermissions),
  );

  if (!canViewInventoryManagement) {
    return null;
  }

  const href = routes.getInventoryHref({ enabled: imsEnabled });
  const friendlyName = newNavigationEnabled
    ? "Inventory"
    : "Inventory management";

  const primaryTab: LinkPrimaryTab = {
    id: "inventory",
    hasNotification: false,
    UnfilledIcon: TagIcon,
    FilledIcon: TagFilledIcon,
    type: "link",
    pathRoot,
    href,
    friendlyName,
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "inventory",
    name: friendlyName,
    NavContent: InventorySecondaryNavigation,
  };

  return { primaryTab, secondaryNavigationData, isActiveProduct: imsEnabled };
}

const InventorySecondaryNavigation = memo(
  function InventorySecondaryNavigation() {
    const { newNavigationEnabled } = useContext(NavigationVersionContext);
    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: newNavigationEnabled ? "Inventory" : "Inventory management",
    });

    return (
      <TreeContext.Provider value={nodeId}>
        <InventoryItem />
      </TreeContext.Provider>
    );
  },
);
